import { type MaybeRefOrGetter, onUnmounted } from 'vue'
import Pusher from 'pusher-js'
import Echo from 'laravel-echo'
import camelcaseKeys from 'camelcase-keys'
import { clone, intersectionBy, uniqBy } from 'es-toolkit'
import { fromPairs, get } from 'es-toolkit/compat'
import { useAuthStore } from '~/stores/auth'
import pusherBatchAuthorizer from '/@overrides/pusher-auth'
import type {
  KeyOfType,
  MaybeArray,
  ModelLike,
  RelationKeys,
  RelationValue
} from '~/types/utils'
import type { AbstractId } from '#shared/types/utils'
import { toArray } from '#app/utils'
import { parseDateResponse } from '~/utils/api-interceptors'

if (import.meta.client) {
  window.Pusher = Pusher
}

export function connectEcho() {
  if (!import.meta.client || window.Echo) {
    return
  }

  const config = useRuntimeConfig()

  const authStore = useAuthStore()

  window.Echo = new Echo({
    broadcaster: 'pusher',
    key: config.public.pusherAppKey,
    cluster: config.public.pusherAppCluster,
    wsHost: config.public.pusherHost,
    wssHost: config.public.pusherHost,
    forceTLS: false,
    encrypted: true,
    disableStats: true,
    enabledTransports: ['ws'],
    namespace: 'App.Events.WebSockets',

    // This is a bit hacky, but we're overriding the pusher-auth layer, so we are in control here
    auth: {
      headers: {
        getToken: (() => authStore.authorizationHeader) as any
      }
    },
    authEndpoint: `${config.public.websocketAuthUrl}auth/batch`,
    authDelay: 200,

    authorizer: pusherBatchAuthorizer
  })
}

export async function disconnectEcho(channel: string = 'tg-website-channel') {
  console.log('Leaving channel')
  await window.Echo.leave(channel)
}

type Models = AbstractId<string> | AbstractId<string>[]

type IdField<T> = KeyOfType<T, AbstractId<string>>

type ModelTracking<T> = {
  [key in IdField<T> | RelationKeys<T>]: Models
}

type CallbackFunction<T> = (message: any, model: Partial<ModelTracking<T>>) => void

interface RelationWebSocket<Model, Parent> {
  id: IdField<Model>
  channel: string
  event: string
  callback: CallbackFunction<Parent>
  notNested?: boolean
}

type RelationObject<T> = IdField<T> | RelationKeys<T> extends never
  ? never
  : {
      [Model in IdField<T> | RelationKeys<T>]?: Model extends IdField<T>
        ? MaybeArray<RelationWebSocket<T, T>>
        : Model extends RelationKeys<T>
          ? MaybeArray<RelationWebSocket<RelationValue<T, Model>, T>>
          : never
    }

interface RelationForEachResult {
  event: string
  channel: string
}

function startListeningInternal<T>(
  model: ModelTracking<T>,
  channel: string,
  event: string,
  callback: CallbackFunction<T>,
  doLog: boolean = true,
  isPublic: boolean = false
) {
  const echoChannel = isPublic
    ? window.Echo.channel(channel)
    : window.Echo.private(channel)
  echoChannel.listen(event, (message?: any) => {
    // Parse keys to camelCase
    const parsedMessage = camelcaseKeys(message, { deep: true })
    parseDateResponse(parsedMessage)
    callback(parsedMessage, model)
  })
  if (doLog) {
    console.log(
      `Subscribed to ${event} on channel ${isPublic ? '' : 'private-'}${channel}`
    )
  }
}

function stopListeningInternal(
  channel: string,
  event: string,
  doLog: boolean = true,
  isPublic: boolean = false
) {
  const echoChannel = isPublic
    ? window.Echo.channel(channel)
    : window.Echo.private(channel)
  echoChannel.stopListening(event)
  if (doLog) {
    console.log(`Stopped listening to ${event} on channel private-${channel}`)
  }
}

function relationsForEach<T extends ModelLike>(
  relations: RelationObject<T>,
  model: ModelTracking<T>,
  lambda: (channel: string, event: string, callback: CallbackFunction<T>) => void
): RelationForEachResult[] {
  return Object.keys(relations).flatMap((relationKey) => {
    const retrievedRelations: MaybeArray<
      RelationWebSocket<RelationValue<T, RelationKeys<T>>, T>
    > = relations[relationKey as RelationKeys<T>]!

    return toArray<RelationWebSocket<RelationValue<T, RelationKeys<T>>, T>>(
      retrievedRelations
    )
      .filter((relation) => {
        let createdRelationId: string
        if (relation.notNested) {
          createdRelationId = String(relation.id)
        } else {
          createdRelationId = `${relationKey}.${String(relation.id)}`
        }
        const relationId = get(model, createdRelationId)
        return !!relationId && relationId > 0
      })
      .flatMap((relation) => {
        let createdRelationId: string
        if (relation.notNested) {
          createdRelationId = String(relation.id)
        } else {
          createdRelationId = `${relationKey}.${String(relation.id)}`
        }
        const relationId = get(model, createdRelationId)

        const callback = relation.callback

        if (Array.isArray(relationId)) {
          return relationId.map((singleRelation) => {
            const channel = `${relation?.channel}-${singleRelation}`
            lambda(channel, relation.event, callback)

            return {
              event: relation.event,
              channel: channel
            }
          })
        } else {
          const channel = `${relation?.channel}-${relationId}`
          lambda(channel, relation.event, callback)

          return {
            event: relation.event,
            channel: channel
          }
        }
      })
  })
}

type UseWebSocketModelEventParams<T extends ModelLike> =
  | {
      event: string
      callback?: CallbackFunction<T>
    }
  | {
      event: Record<string, CallbackFunction<T>>
      callback?: never
    }
type UseWebSocketModelParams<T extends ModelLike> = {
  baseChannel: string
  idField?: IdField<T>
  relations?: RelationObject<T>
  cancelOnUnmounted?: boolean
  debug?: boolean
} & UseWebSocketModelEventParams<T>

export function useWebSocketModel<T extends ModelLike>(
  params: UseWebSocketModelParams<T>
) {
  const {
    baseChannel,
    event,
    callback = undefined,
    idField = 'id' as IdField<T>,
    relations = undefined,
    cancelOnUnmounted = true,
    debug = import.meta.env.DEV
  } = params

  const userStore = useUsersStore()

  if (!window.Echo) {
    connectEcho()
  }

  let eventMap: Record<string, CallbackFunction<T>> = {}

  if (typeof event === 'string') {
    if (!callback) {
      throw new Error('No callback specified!')
    }
    eventMap[event] = callback
  } else {
    eventMap = event // Technically eventMapping
  }

  let modelArray: ModelTracking<T>[] = []

  const setModel = (newModel: MaybeRefOrGetter<T>) => {
    setModels([toValue(newModel)])
  }

  const setModels = (newModels: MaybeRefOrGetter<MaybeArray<T>>) => {
    if (!userStore.specificUser) {
      return
    }

    newModels = toArray(toValue(newModels))

    const workWithModels = clone(newModels).map<ModelTracking<T>>((model) => {
      let relationMapping: Record<RelationKeys<T>, Models> | undefined
      if (relations) {
        relationMapping = fromPairs(
          Object.keys(relations).flatMap((relationKey) => {
            const retrievedRelations = relations[relationKey as keyof typeof relations]!

            return toArray(retrievedRelations)
              .map((relation) => {
                let createdRelationKey: string
                if (relation.notNested) {
                  createdRelationKey = String(relation.id)
                } else {
                  createdRelationKey = `${relationKey}.${String(relation.id)}`
                }

                let relationId: Models = get(model, createdRelationKey) as AbstractId<any>

                // Relation must be an array
                if (!relationId) {
                  const maybeRelationArray = get(model, relationKey) as any[] | undefined
                  if (maybeRelationArray && Array.isArray(maybeRelationArray)) {
                    relationId = maybeRelationArray.map(
                      (nestedRelation) =>
                        nestedRelation[relation.id] as AbstractId<string>
                    )
                  } else if (debug) {
                    console.warn(
                      `Path ${createdRelationKey} could not be found in model:`,
                      model
                    )
                  }
                }

                return [createdRelationKey, relationId]
              })
              .filter(([_unused, relationId]) => !!relationId)
          })
        ) as Record<RelationKeys<T>, AbstractId<any>>
      }
      return {
        [idField]: model[idField],
        ...relationMapping
      } as ModelTracking<T>
    })

    const intersectedModels = new Set(
      intersectionBy(modelArray, workWithModels, (m) => m[idField]).map(
        (model) => model[idField]
      )
    )

    const listenToNewModels = workWithModels.filter(
      (model) => !intersectedModels.has(model[idField])
    )
    const stopListeningTo = modelArray.filter(
      (model) => !intersectedModels.has(model[idField])
    )

    stopListeningSpecific(stopListeningTo)

    modelArray = workWithModels

    Object.keys(eventMap).forEach((event) => {
      const callback = eventMap[event]

      const uniqueModels = uniqBy(listenToNewModels, (m) => m[idField])
      uniqueModels.forEach((model) => {
        const channel = `${baseChannel}-${model[idField].toString()}`

        startListeningInternal(
          model,
          channel,
          event,
          callback,
          debug && listenToNewModels.length === 1
        )
      })

      if (debug && uniqueModels.length > 1) {
        const joinedChannels = uniqueModels.map((model) => model[idField]).join(', ')

        console.log(
          `Subscribed to ${event} on channel private-${baseChannel}: ${joinedChannels}`
        )
      }
    })

    const relationResult: RelationForEachResult[] = []
    listenToNewModels.forEach((model) => {
      if (relations) {
        const result = relationsForEach(relations, model, (channel, event, callback) => {
          startListeningInternal(model, channel, event, callback, false)
        })

        relationResult.push(...result)
      }
    })
    if (debug && relationResult.length > 0) {
      console.table(relationResult.map((result) => ({ ...result, type: 'started' })))
    }
  }

  const getModel = (): ModelTracking<T> | undefined => {
    if (modelArray.length > 1) {
      throw new Error('Models is Array, use getModels')
    }
    if (modelArray.length === 0) {
      return undefined
    }

    return modelArray[0]
  }

  const getModels = (): ModelTracking<T>[] | undefined => {
    if (modelArray.length === 1) {
      throw new Error('Models is not an Array, use getModel')
    }
    if (modelArray.length === 0) {
      return undefined
    }
    return modelArray
  }

  const stopListening = () => {
    if (!userStore.specificUser) {
      return
    }

    stopListeningSpecific(modelArray)
  }

  const stopListeningSpecific = (models: ModelTracking<T>[]) => {
    Object.keys(eventMap).forEach((event) => {
      const uniqueModels = uniqBy(models, (m) => m[idField])
      uniqueModels.forEach((model) => {
        const channel = `${baseChannel}-${model[idField].toString()}`

        stopListeningInternal(channel, event, debug && models.length === 1)
      })

      if (debug && uniqueModels.length > 1) {
        const leftChannels = uniqueModels.map((model) => model[idField]).join(', ')

        console.log(
          `Stopped listening to ${event} on channel private-${baseChannel}: ${leftChannels}`
        )
      }
    })

    const relationResult: RelationForEachResult[] = []
    models.forEach((model) => {
      if (relations) {
        const result = relationsForEach(relations, model, (channel, event) => {
          stopListeningInternal(channel, event, false)
        })

        relationResult.push(...result)
      }
    })
    if (debug && relationResult.length > 0) {
      console.table(relationResult.map((result) => ({ ...result, type: 'stopped' })))
    }

    modelArray = modelArray.filter(
      (model) =>
        !models.some((internalModel) => internalModel[idField] === model[idField])
    )
  }

  if (getCurrentInstance() && cancelOnUnmounted) {
    onUnmounted(() => {
      // Only stop listening if we're still logged in (else it's being taken care of by (disconnectEcho))
      if (userStore.specificUser) {
        stopListening()
      }
    })
  }

  return {
    getModel,
    getModels,
    setModel,
    setModels,
    stopListening
  }
}

interface UseWebSocketParams {
  event: string
  callback: CallbackFunction<never>
  cancelOnUnmounted?: boolean
  channel?: string
  delayStart?: boolean
  debug?: boolean
  isPublic?: boolean
}

export function useWebSocket(params: UseWebSocketParams) {
  const {
    event,
    callback,
    cancelOnUnmounted = true,
    channel = 'tg-website-channel',
    delayStart = false,
    debug = import.meta.env.DEV,
    isPublic = false
  } = params

  const userStore = useUsersStore()

  let internalChannel = channel

  const isListening = ref(false)

  if (!window.Echo) {
    connectEcho()
  }

  const setChannel = (newChannel: string) => {
    stopListeningInternal(internalChannel, event, debug, isPublic)
    internalChannel = newChannel
    startListeningInternal(
      { id: -1 as AbstractId<never> },
      internalChannel,
      event,
      callback,
      debug,
      isPublic
    )
  }

  const startListening = () => {
    if (isListening.value) {
      return
    }
    startListeningInternal(
      { id: -1 as AbstractId<never> },
      internalChannel,
      event,
      callback,
      debug,
      isPublic
    )
    isListening.value = true
  }
  if (!delayStart) {
    startListening()
  }

  const stopListening = () => {
    isListening.value = false
    stopListeningInternal(internalChannel, event, debug, isPublic)
  }

  if (getCurrentInstance() && cancelOnUnmounted) {
    onUnmounted(() => {
      // Only stop listening if we're still logged in (else it's being taken care of by (disconnectEcho)
      if (userStore.specificUser) {
        stopListening()
      }
    })
  }

  return {
    startListening,
    stopListening,
    setChannel,
    isListening
  }
}
